import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  classifyQuery,
  searchKnowledge,
} from "@/shared/lib/search/svc";
import type {
  KnowledgeSearchFilters,
  SearchFlowClassificationResponse,
} from "@/shared/lib/search/interfaces";
import useAppFocus from "@/shared/hooks/useAppFocus";
import { useSettingsContext } from "@/app/providers/SettingsProvider";
import { useUser } from "@/app/providers/UserProvider";
import { useSearchHistory } from "@/shared/hooks/useSearchHistory";
import {
  QueryControllerContext,
  QueryControllerValue,
  QueryState,
  AppMode,
} from "@/app/providers/QueryControllerContext";

// CR-013 (SOL-013 §3.1/§3.2): Search UI is core Phase-1, not an EE add-on.
// This file used to be a thin `eeGated(EEQueryControllerProvider)` re-export
// pointing at `@/ee/providers/QueryControllerProvider` — that file is now
// deleted and its implementation lives here directly, with every
// `isPaidEnterpriseFeaturesEnabled`/`usePaidEnterpriseFeaturesEnabled`
// reference removed (6 points in the original: declaration, effect
// condition, effect deps, `setAppMode` guard, `setAppMode` deps, `submit`
// guard, `submit` deps). `searchResults` now uses `KnowledgeSearchResult[]`
// (row-based Knowledge Query API, `searchKnowledge()`) instead of
// `SearchDocWithContent[]` (chat-document-search, `searchDocuments()`), and
// `llmSelectedDocIds` — a chat-document-search-only concept with no
// equivalent in row search — is gone (see QueryControllerContext.ts).

export {
  QueryControllerContext,
  useQueryController,
} from "./QueryControllerContext";
export type {
  AppMode,
  QueryState,
  QueryControllerValue,
} from "./QueryControllerContext";

interface QueryControllerProviderProps {
  children: React.ReactNode;
}

export function QueryControllerProvider({
  children,
}: QueryControllerProviderProps) {
  const appFocus = useAppFocus();
  const settings = useSettingsContext();
  const { isSearchModeAvailable: searchUiEnabled } = settings;
  const { user } = useUser();
  const { record: recordSearchHistory } = useSearchHistory();

  // ── Merged query state (discriminated union) ──────────────────────────
  const [state, setState] = useState<QueryState>({
    phase: "idle",
    appMode: "chat",
  });

  // Persistent app-mode preference — survives phase transitions and is
  // used to restore the correct mode when resetting back to idle.
  const appModeRef = useRef<AppMode>("chat");

  // ── App mode sync from user preferences ───────────────────────────────
  // Kept from the pre-CR-013 ee/ implementation (not an EE gate — this is
  // "restore the user's saved default app-mode", unrelated to licensing).
  // Only `isPaidEnterpriseFeaturesEnabled` is dropped from the condition.
  const persistedMode = user?.preferences?.default_app_mode;

  useEffect(() => {
    let mode: AppMode = "chat";
    if (searchUiEnabled && persistedMode) {
      const lower = persistedMode.toLowerCase();
      mode = (["auto", "search", "chat"] as const).includes(lower as AppMode)
        ? (lower as AppMode)
        : "chat";
    }
    appModeRef.current = mode;
    setState((prev) =>
      prev.phase === "idle" ? { phase: "idle", appMode: mode } : prev
    );
  }, [searchUiEnabled, persistedMode]);

  const setAppMode = useCallback(
    (mode: AppMode) => {
      if (!searchUiEnabled) return; // CHỈ còn 1 điều kiện, không còn isPaidEnterpriseFeaturesEnabled
      setState((prev) => {
        if (prev.phase !== "idle") return prev;
        appModeRef.current = mode;
        return { phase: "idle", appMode: mode };
      });
    },
    [searchUiEnabled]
  );

  // ── Ancillary state ───────────────────────────────────────────────────
  const [query, setQuery] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<
    QueryControllerValue["searchResults"]
  >([]);
  const [error, setError] = useState<string | null>(null);

  // Abort controllers for in-flight requests
  const classifyAbortRef = useRef<AbortController | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);

  /**
   * Perform Knowledge Query search (pure data-fetching, no phase side effects).
   */
  const performSearch = useCallback(
    async (searchQuery: string, filters?: KnowledgeSearchFilters): Promise<void> => {
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }

      const controller = new AbortController();
      searchAbortRef.current = controller;

      try {
        const response = await searchKnowledge(searchQuery, {
          source: filters?.source,
          owner: filters?.owner,
          type: filters?.type,
          from: filters?.from,
          to: filters?.to,
          limit: 20,
          page: 1,
          signal: controller.signal,
        });

        setError(null);
        setSearchResults(response.results);
        recordSearchHistory(searchQuery);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          throw err;
        }

        setError("Search failed. Please try again.");
        setSearchResults([]);
      }
    },
    [recordSearchHistory]
  );

  /**
   * Classify a query as search or chat
   */
  const performClassification = useCallback(
    async (classifyQueryText: string): Promise<"search" | "chat"> => {
      if (classifyAbortRef.current) {
        classifyAbortRef.current.abort();
      }

      const controller = new AbortController();
      classifyAbortRef.current = controller;

      try {
        const response: SearchFlowClassificationResponse = await classifyQuery(
          classifyQueryText,
          controller.signal
        );

        const result = response.is_search_flow ? "search" : "chat";
        return result;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          throw error;
        }

        setError("Query classification failed. Falling back to chat.");
        return "chat";
      }
    },
    []
  );

  /**
   * Submit a query - routes based on app mode
   */
  const submit = useCallback(
    async (
      submitQuery: string,
      onChat: (query: string) => void,
      filters?: KnowledgeSearchFilters
    ): Promise<void> => {
      setQuery(submitQuery);
      setError(null);

      const currentAppMode = appModeRef.current;

      // Always route through chat if:
      // 1. Admin has disabled the Search UI (or no synced connector)
      // 2. Not in the "New Session" tab
      // 3. In "New Session" tab but app-mode is "Chat"
      if (
        !searchUiEnabled ||
        !appFocus.isNewSession() ||
        currentAppMode === "chat"
      ) {
        setState({ phase: "chat" });
        setSearchResults([]);
        onChat(submitQuery);
        return;
      }

      // Search mode: immediately show SearchUI with loading state
      if (currentAppMode === "search") {
        setState({ phase: "searching" });
        try {
          await performSearch(submitQuery, filters);
        } catch (err) {
          if (err instanceof Error && err.name === "AbortError") return;
          throw err;
        }
        setState({ phase: "search-results" });
        return;
      }

      // Auto mode: classify first, then route
      setState({ phase: "classifying" });
      try {
        const result = await performClassification(submitQuery);

        if (result === "search") {
          setState({ phase: "searching" });
          await performSearch(submitQuery, filters);
          setState({ phase: "search-results" });
          appModeRef.current = "search";
        } else {
          setState({ phase: "chat" });
          setSearchResults([]);
          onChat(submitQuery);
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }

        setState({ phase: "chat" });
        setSearchResults([]);
        onChat(submitQuery);
      }
    },
    [appFocus, performClassification, performSearch, searchUiEnabled]
  );

  /**
   * Re-run the current search query with updated server-side filters
   */
  const refineSearch = useCallback(
    async (filters: KnowledgeSearchFilters): Promise<void> => {
      if (!query) return;
      setState({ phase: "searching" });
      try {
        await performSearch(query, filters);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        throw err;
      }
      setState({ phase: "search-results" });
    },
    [query, performSearch]
  );

  /**
   * Reset all state to initial values
   */
  const reset = useCallback(() => {
    if (classifyAbortRef.current) {
      classifyAbortRef.current.abort();
      classifyAbortRef.current = null;
    }
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }

    setQuery(null);
    setState({ phase: "idle", appMode: appModeRef.current });
    setSearchResults([]);
    setError(null);
  }, []);

  const value: QueryControllerValue = useMemo(
    () => ({
      state,
      setAppMode,
      searchResults,
      error,
      submit,
      refineSearch,
      reset,
    }),
    [state, setAppMode, searchResults, error, submit, refineSearch, reset]
  );

  // Sync state with navigation context
  useEffect(reset, [appFocus, reset]);

  return (
    <QueryControllerContext.Provider value={value}>
      {children}
    </QueryControllerContext.Provider>
  );
}
